import { useQueryClient } from "@tanstack/react-query";
import type { FastPathReport } from "../../lib/bulkFieldFastPath";
import type { SampleAttachReport } from "../../lib/sampleFastPath";
import { targetAttachLine } from "../../lib/targetPointLabel";
import type { TargetAttachReport } from "../../lib/targetPointsLane";
import type {
  ZoneAttachReport,
  ZoneResultAttachReport,
} from "../../lib/zoneFastPath";
import { cachedLayerRows } from "../../lib/layerLoad";

interface UploadSuccessProps {
  fastReport: FastPathReport | null;
  sampleReport: SampleAttachReport | null;
  zoneReport: ZoneAttachReport | null;
  zoneResultReport: ZoneResultAttachReport | null;
  targetReport?: TargetAttachReport | null;
  yieldFiles: string[];
  onDone: () => void;
}

interface SuccessCounts {
  createdN: number;
  matchedN: number;
  samplesN: number;
  zonesN: number;
  zoneResultsN: number;
  targetsN: number;
  yieldN: number;
}

function pointsOf(
  report: { attached: { points: number }[] } | null | undefined,
): number {
  if (!report) return 0;
  return report.attached.reduce((n, row) => n + row.points, 0);
}

function successCounts(props: UploadSuccessProps): SuccessCounts {
  return {
    createdN: props.fastReport?.created.length ?? 0,
    matchedN: props.fastReport?.matchedExisting.length ?? 0,
    samplesN:
      props.sampleReport?.attached.reduce((n, a) => n + a.points, 0) ?? 0,
    zonesN: props.zoneReport?.attached.reduce((n, a) => n + a.zones, 0) ?? 0,
    zoneResultsN:
      props.zoneResultReport?.attached.reduce((n, a) => n + a.zones, 0) ?? 0,
    targetsN: pointsOf(props.targetReport),
    yieldN: props.yieldFiles.length,
  };
}

function nothingImported(n: SuccessCounts): boolean {
  return (
    n.createdN === 0 &&
    n.matchedN === 0 &&
    n.samplesN === 0 &&
    n.zonesN === 0 &&
    n.zoneResultsN === 0 &&
    n.targetsN === 0 &&
    n.yieldN === 0
  );
}

function CreatedLine({ n, report }: { n: number; report: FastPathReport }) {
  if (n <= 0) return null;
  return (
    <li>
      {n} field{n === 1 ? "" : "s"} created —{" "}
      {report.created.map((c) => c.field.field_name).join(" · ")}
    </li>
  );
}

function MatchedLine({ n, report }: { n: number; report: FastPathReport }) {
  if (n <= 0) return null;
  return (
    <li>
      {`${n} boundar${n === 1 ? "y" : "ies"} matched your existing field${n === 1 ? "" : "s"} — `}
      {report.matchedExisting
        .map((m) => m.hits[0]?.field_name ?? m.candidate.name)
        .join(" · ")}
    </li>
  );
}

function SamplesLine({ n, report }: { n: number; report: SampleAttachReport }) {
  if (n <= 0) return null;
  return (
    <li>
      {n} soil sample{n === 1 ? "" : "s"} attached —{" "}
      {report.attached
        .map((a) => `${a.file} → ${a.fieldName} (${a.points})`)
        .join(" · ")}
    </li>
  );
}

function ZonesLine({ n, report }: { n: number; report: ZoneAttachReport }) {
  if (n <= 0) return null;
  return (
    <li>
      {n} zone{n === 1 ? "" : "s"} saved —{" "}
      {report.attached
        .map((a) => `${a.file} → ${a.fieldName} (${a.zones})`)
        .join(" · ")}
    </li>
  );
}

function ZoneResultsLine({
  n,
  report,
}: {
  n: number;
  report: ZoneResultAttachReport;
}) {
  if (n <= 0) return null;
  return (
    <li>
      Zone results attached —{" "}
      {report.attached.map((a) => `${a.file} → ${a.fieldName}`).join(" · ")}
    </li>
  );
}

function TargetLines({ report }: { report: TargetAttachReport }) {
  const qc = useQueryClient();
  if (!report.attached.length) return null;
  return (
    <>
      {report.attached.map((a) => (
        <li key={`${a.file}:${a.fieldId}`}>
          {targetAttachLine(a, cachedLayerRows(qc, a.fieldId))}
        </li>
      ))}
    </>
  );
}

function YieldLine({ n, files }: { n: number; files: string[] }) {
  if (n <= 0) return null;
  return (
    <li>
      {n} yield map{n === 1 ? "" : "s"} sent for fast import —{" "}
      {files.join(" · ")}
    </li>
  );
}

function UploadSuccessLines(props: UploadSuccessProps) {
  const n = successCounts(props);
  return (
    <>
      {props.fastReport && (
        <CreatedLine n={n.createdN} report={props.fastReport} />
      )}
      {props.fastReport && (
        <MatchedLine n={n.matchedN} report={props.fastReport} />
      )}
      {props.sampleReport && (
        <SamplesLine n={n.samplesN} report={props.sampleReport} />
      )}
      {props.zoneReport && <ZonesLine n={n.zonesN} report={props.zoneReport} />}
      {props.zoneResultReport && (
        <ZoneResultsLine n={n.zoneResultsN} report={props.zoneResultReport} />
      )}
      {props.targetReport && <TargetLines report={props.targetReport} />}
      <YieldLine n={n.yieldN} files={props.yieldFiles} />
      {nothingImported(n) && <li>Nothing needed importing.</li>}
    </>
  );
}

export function UploadSuccess(props: UploadSuccessProps) {
  return (
    <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto">
      <div className="rounded border border-emerald-500/40 bg-emerald-500/5 px-3 py-2 text-[12px]">
        <div className="font-medium text-fg">
          ✓ All set — everything in this drop was handled in real time
        </div>
        <ul className="mt-1 text-[11px] text-muted">
          <UploadSuccessLines {...props} />
        </ul>
        <p className="mt-1 text-[10px] text-faint">
          Nothing was handed off for deeper analysis. New fields start preparing
          their soil data right away.
        </p>
      </div>
      <button
        className="self-start rounded border border-line px-2 py-1 text-xs text-muted hover:bg-hover hover:text-fg"
        onClick={props.onDone}
      >
        Done
      </button>
    </div>
  );
}
